import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useTableSort } from "@/lib/tableSort";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Zap, Landmark, Target, CheckCircle2, TrendingUp, Gauge, Download, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";

const GREEN = "#16A34A";
const NAVY = "#0F2C59";
const AMBER = "#D97706";

function StatCard({ icon: Icon, label, value, color, testid }) {
  return (
    <Card className="p-4 flex items-center gap-3" data-testid={testid}>
      <div className="h-11 w-11 rounded-xl grid place-items-center shrink-0" style={{ background: `${color}1a`, color }}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-2xl font-bold" style={{ color }}>{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </Card>
  );
}

export default function Efficiency() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("field");
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState(61);

  const download = async (format) => {
    setBusy(true);
    try {
      const order = effT.sorted.map((r) => r.committee_no).join(",");
      const res = await api.get("/dashboard/council-efficiency/export", { params: { format, mode: tab, order, only: 1 }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `verimlilik_${tab}.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Dışa aktarma başarısız");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    api.get("/dashboard/committee-scorecard")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Veriler yüklenemedi"));
  }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    const list = (data.rows || [])
      .filter((r) => (r.council_seats || 0) > 0 && (r.faal || 0) > 0)
      .map((r) => {
        const m = tab === "field" ? r.field : tab === "worst" ? r.worst : r.forecast;
        const seats = r.council_seats;
        const secured = m?.bize || 0;
        const target = m?.win_target || 0;
        const remaining = Math.max(target - secured, 0);
        const noList = !!r.no_list;
        return {
          committee_no: r.committee_no, name: r.name, seats, secured, target, remaining,
          perSeat: seats ? remaining / seats : Infinity,
          ratio: target > 0 ? secured / target : 0,
          winning: remaining === 0 && !noList,
          noList, kesin: r.kesin || 0, minKesin: r.min_kesin_for_list || 10,
        };
      })
      .sort((a, b) => {
        if (a.noList !== b.noList) return a.noList ? 1 : -1;
        if (a.winning !== b.winning) return a.winning ? -1 : 1;
        if (a.winning && b.winning) {
          if (b.ratio !== a.ratio) return b.ratio - a.ratio;
          return b.seats - a.seats;
        }
        if (a.perSeat !== b.perSeat) return a.perSeat - b.perSeat;
        return b.seats - a.seats;
      });
    let cumSeats = 0, cumVotes = 0;
    return list.map((r) => {
      cumSeats += r.seats;
      cumVotes += r.remaining;
      return { ...r, cumSeats, cumVotes };
    });
  }, [data, tab]);

  const totalSeats = rows.reduce((s, r) => s + r.seats, 0);
  const wonSeats = rows.filter((r) => r.winning).reduce((s, r) => s + r.seats, 0);
  const noListRows = rows.filter((r) => r.noList);
  const lostSeats = noListRows.reduce((s, r) => s + r.seats, 0);
  const totalRemaining = rows.filter((r) => !r.noList).reduce((s, r) => s + r.remaining, 0);

  const allSeats = useMemo(
    () => (data?.rows || []).reduce((s, r) => s + (r.council_seats || 0), 0),
    [data]
  );
  const majority = Math.floor(allSeats / 2) + 1;

  // N koltuk için minimum ek oy: verimlilik sırasına göre komiteler toplanır
  const minVotesFor = (n) => {
    if (n <= 0) return { votes: 0, committees: 0, seats: 0, reachable: true };
    let seats = 0, votes = 0, committees = 0;
    for (const r of rows) {
      if (r.noList) continue;
      seats += r.seats; votes += r.remaining; committees += 1;
      if (seats >= n) return { votes, committees, seats, reachable: true };
    }
    return { votes, committees, seats, reachable: false };
  };

  const majorityPlan = minVotesFor(majority);
  const allPlan = minVotesFor(allSeats);
  const pickPlan = minVotesFor(pick);

  // Çoğunluğa ulaşılan satır indeksi
  const majorityRowIdx = majorityPlan.reachable ? majorityPlan.committees - 1 : -1;
  const effT = useTableSort(rows);

  return (
    <div className="space-y-6" data-testid="efficiency-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: `${AMBER}1a`, color: AMBER }}>
            <Zap className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0F2C59] dark:text-sky-300">Meclis Üyesi Verimliliği</h1>
            <p className="text-sm text-muted-foreground">En az oyla en çok meclis üyesi — koltuk başına gereken oya göre sıralı</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => download("xlsx")} disabled={busy} data-testid="eff-export-xlsx">
            <Download className="h-4 w-4 mr-1.5" /> Excel
          </Button>
          <Button variant="outline" onClick={() => download("pdf")} disabled={busy} data-testid="eff-export-pdf">
            <FileText className="h-4 w-4 mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      <div className="inline-flex rounded-lg border border-border p-1 bg-muted/40" data-testid="efficiency-tabs">
        {[["field", "Saha (kesin oy)"], ["worst", "Kötü İhtimal"], ["forecast", "2026 Tahmini"]].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`efficiency-tab-${k}`}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === k ? "bg-[#0F2C59] text-white" : "text-muted-foreground hover:text-foreground"}`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Landmark} label="Toplam meclis üyeliği" value={totalSeats} color={NAVY} testid="eff-total-seats" />
        <StatCard icon={CheckCircle2} label="Şu an kazanılan üyelik" value={wonSeats} color={GREEN} testid="eff-won-seats" />
        <StatCard icon={Target} label={`Liste yok (KESİN<10) kaybedilen üyelik`} value={lostSeats} color="#DC2626" testid="eff-nolist-seats" />
        <StatCard icon={TrendingUp} label="Kazanılabilir komiteler için gereken ek oy" value={totalRemaining} color={NAVY} testid="eff-total-votes" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-5 border-l-4" style={{ borderLeftColor: GREEN }} data-testid="eff-majority-card">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Çoğunluk hedefi</div>
          <div className="mt-1 text-sm font-medium">
            {majority} meclis üyesi ({allSeats} üyelikte çoğunluk) için gereken minimum ek oy
          </div>
          <div className="mt-2 text-3xl font-bold" style={{ color: GREEN }} data-testid="eff-majority-votes">
            {majorityPlan.reachable ? `+${majorityPlan.votes}` : "—"}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {majorityPlan.reachable
              ? `En verimli ${majorityPlan.committees} komite kazanılırsa ${majorityPlan.seats} üyelik`
              : "Mevcut veriyle ulaşılamıyor"}
          </div>
        </Card>
        <Card className="p-5 border-l-4" style={{ borderLeftColor: NAVY }} data-testid="eff-all-card">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Tam kadro</div>
          <div className="mt-1 text-sm font-medium">
            {allSeats} meclis üyeliğinin tamamı için gereken minimum ek oy
          </div>
          <div className="mt-2 text-3xl font-bold" style={{ color: NAVY }} data-testid="eff-all-votes">
            {allPlan.reachable ? `+${allPlan.votes}` : `+${allPlan.votes}*`}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {allPlan.reachable
              ? `Tüm ${allPlan.committees} komitenin kazanılması gerekir`
              : `Listedeki ${allPlan.committees} komite ile ${allPlan.seats} üyelik hesaplanabiliyor`}
          </div>
        </Card>
      </div>

      <Card className="p-5" data-testid="eff-picker-card">
        <div className="flex flex-col lg:flex-row lg:items-center gap-5">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[#0F2C59] dark:text-sky-300">Meclis üyesi sayısı seç</div>
            <div className="flex items-center gap-3 mt-3">
              <input
                type="range"
                min={1}
                max={Math.max(allSeats, 1)}
                value={pick}
                onChange={(e) => setPick(Number(e.target.value))}
                className="flex-1 accent-[#0F2C59]"
                data-testid="eff-seat-slider"
              />
              <input
                type="number"
                min={1}
                max={Math.max(allSeats, 1)}
                value={pick}
                onChange={(e) => setPick(Math.min(Math.max(Number(e.target.value) || 1, 1), Math.max(allSeats, 1)))}
                className="w-20 rounded-md border border-border bg-background px-2 py-1.5 text-sm text-right tabular-nums"
                data-testid="eff-seat-input"
              />
            </div>
            <div className="flex gap-2 mt-3">
              {[majority, allSeats].map((n) => (
                <Button key={n} size="sm" variant="outline" onClick={() => setPick(n)} data-testid={`eff-seat-preset-${n}`}>
                  {n} üye
                </Button>
              ))}
            </div>
          </div>
          <div className="lg:border-l lg:pl-6 border-border">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              {pick} meclis üyesi için gereken minimum oy
            </div>
            <div className="text-3xl font-bold mt-1" style={{ color: pickPlan.reachable ? AMBER : "#DC2626" }} data-testid="eff-pick-votes">
              {pickPlan.reachable ? `+${pickPlan.votes}` : "Ulaşılamaz"}
            </div>
            <div className="text-xs text-muted-foreground mt-1" data-testid="eff-pick-detail">
              {pickPlan.reachable
                ? `${pickPlan.committees} komite · toplam ${pickPlan.seats} üyelik`
                : `Mevcut veriyle en fazla ${pickPlan.seats} üyelik`}
            </div>
          </div>
        </div>
      </Card>

      {!data ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="efficiency-table">
              <thead>
                <tr className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                  <th className="px-4 py-3 font-medium">#</th>
                  <effT.Th sortKey="e-komite" get={(r) => r.name} text className="px-4 py-3 font-medium">Komite</effT.Th>
                  <effT.Th sortKey="e-seats" get={(r) => r.seats} className="px-4 py-3 font-medium text-center">Meclis Üyesi</effT.Th>
                  <effT.Th sortKey="e-secured" get={(r) => r.secured} className="px-4 py-3 font-medium text-right">Kesin Oy</effT.Th>
                  <effT.Th sortKey="e-target" get={(r) => r.target} className="px-4 py-3 font-medium text-right">Hedef Oy</effT.Th>
                  <effT.Th sortKey="e-ratio" get={(r) => r.ratio} className="px-4 py-3 font-medium text-right">Kesin/Hedef %</effT.Th>
                  <effT.Th sortKey="e-remaining" get={(r) => r.remaining} lowBest className="px-4 py-3 font-medium text-right">Kalan Oy</effT.Th>
                  <effT.Th sortKey="e-perseat" get={(r) => (r.winning ? 0 : r.perSeat)} lowBest className="px-4 py-3 font-medium text-right">
                    <span className="inline-flex items-center gap-1"><Gauge className="h-3.5 w-3.5" /> Koltuk/Oy</span>
                  </effT.Th>
                  <effT.Th sortKey="e-cumseats" get={(r) => r.cumSeats} className="px-4 py-3 font-medium text-right">Kümülatif Üye</effT.Th>
                  <effT.Th sortKey="e-cumvotes" get={(r) => r.cumVotes} className="px-4 py-3 font-medium text-right">Kümülatif Oy</effT.Th>
                  <effT.Th sortKey="e-durum" get={(r) => (r.winning ? 1 : 0)} className="px-4 py-3 font-medium text-center">Durum</effT.Th>
                </tr>
              </thead>
              <tbody>
                {effT.sorted.map((r, i) => (
                  <React.Fragment key={r.committee_no}>
                  <tr className={`border-t border-border/60 hover:bg-muted/40 ${r.noList ? "bg-red-50/70 dark:bg-red-950/20" : ""}`} data-testid={`efficiency-row-${r.committee_no}`}>
                    <td className="px-4 py-3 text-muted-foreground tabular-nums">{i + 1}</td>
                    <td className="px-4 py-3">
                      <span className="text-muted-foreground">#{r.committee_no}</span> <span className="font-medium">{r.name}</span>
                    </td>
                    <td className="px-4 py-3 text-center tabular-nums font-bold text-[#0F2C59] dark:text-sky-300">{r.seats}</td>
                    <td className="px-4 py-3 text-right tabular-nums" style={{ color: GREEN }}>{r.secured}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{r.target}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium" style={{ color: r.ratio >= 1 ? GREEN : NAVY }} data-testid={`efficiency-ratio-${r.committee_no}`}>
                      {r.target > 0 ? `%${Math.round(r.ratio * 100)}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold" style={{ color: r.remaining === 0 ? GREEN : AMBER }}>
                      {r.remaining === 0 ? "0" : `+${r.remaining}`}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-bold" style={{ color: r.winning ? GREEN : NAVY }}>
                      {r.winning ? "—" : r.perSeat.toFixed(1)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">{r.cumSeats}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">{r.cumVotes}</td>
                    <td className="px-4 py-3 text-center">
                      {r.noList ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
                          style={{ background: "#DC26261a", color: "#DC2626" }}
                          title={`KESİN oy ${r.kesin} · liste için en az ${r.minKesin} gerekir (5 asil + 5 yedek)`}
                          data-testid={`efficiency-nolist-${r.committee_no}`}>
                          Liste yok (KESİN&lt;{r.minKesin})
                        </span>
                      ) : r.winning ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: `${GREEN}1a`, color: GREEN }}>
                          <CheckCircle2 className="h-3 w-3" /> Kazanılmış
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">{r.perSeat.toFixed(1)} oy/üye</span>
                      )}
                    </td>
                  </tr>
                  {i === majorityRowIdx && !effT.sort && (
                    <tr data-testid="efficiency-majority-marker">
                      <td colSpan={11} className="px-4 py-2 text-xs font-semibold" style={{ background: `${GREEN}14`, color: GREEN }}>
                        ▲ {majority}. koltuk burada — çoğunluk için gereken minimum ek oy: +{majorityPlan.votes}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={11} className="px-4 py-10 text-center text-muted-foreground">Veri bulunamadı</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <p className="text-xs text-muted-foreground">
        "Koltuk/Oy" = bir komiteyi kazanmak için kalan ek oy ÷ o komitenin meclis üyeliği. Düşük değer = az eforla çok üye.
        Zaten hedefte olan komiteler en üstte (kazanılmış) gösterilir. "Kümülatif" sütunları, yukarıdan aşağı seçildiğinde
        toplam kaç üye için toplam kaç ek oy gerektiğini gösterir. {tab === "field"
          ? "Saha modu: KESİN + yetki belgesi güvencesi baz alınır."
          : tab === "worst"
            ? "Kötü ihtimal: 2026 tahmininde bize oy verecek olanların %10'u karşıya geçer, %10'u sandığa gelmez (Genel Panel'deki Kötü Senaryo ile aynı)."
            : "Tahmin modu: 2026 seçim tahmini baz alınır."}
        {" "}KESİN oyu 10'un altında olan komitede 5 asil + 5 yedek üyelik liste çıkarılamayacağı için komite
        doğrudan kaybedilmiş sayılır; o komiteler listenin sonunda kırmızı gösterilir ve hedef planlarına dahil edilmez.
      </p>
    </div>
  );
}
